import React, { useState } from "react";
import {
  FiBold,
  FiDroplet,
  FiItalic,
  FiLink,
  FiRotateCcw,
  FiType,
  FiUnderline,
} from "react-icons/fi";

import {
  EMAIL_FONT_SIZES,
  EMAIL_HIGHLIGHTS,
  EMAIL_TEXT_COLORS,
} from "./emailBodyFormat";

/**
 * The email body's formatting row.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE MAY TAKE FOCUS
 * ---------------------------------------------------------------------------
 * Every control preventDefaults its `mousedown`. That one line is what makes
 * the toolbar work at all: a button that takes focus collapses the selection in
 * the `contentEditable` behind it, so by the time `click` fires there is
 * nothing left to embolden. Preventing the default keeps the caret exactly
 * where the author left it.
 *
 * It is also why the size and colour menus are buttons in a popover rather than
 * a `<select>` or an `<input type="color">` — a native control has to take
 * focus to open, and neither can be talked out of it.
 *
 * Presentational: it renders controls and calls back. Every command lives in
 * `emailBodyFormat.js` and every DOM write is the editor's.
 */

const BUTTON =
  "inline-flex h-7 min-w-[28px] items-center justify-center gap-1 rounded-md " +
  "border border-transparent px-1.5 text-bc-text-2 transition-colors " +
  "hover:bg-bc-neut-bg hover:text-bc-text focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-bc-soft";

const BUTTON_ON = "border-bc-border bg-white text-bc-text";

const POPOVER =
  "absolute left-0 top-[calc(100%+6px)] z-10 rounded-lg border " +
  "border-bc-border bg-white p-2 shadow-sm";

const SWATCH =
  "h-6 w-6 rounded-md border border-bc-border transition-transform " +
  "hover:scale-110 focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-bc-soft";

const SIZE_ITEM =
  "block w-full rounded-md px-2.5 py-1.5 text-left text-bc-text-2 " +
  "transition-colors hover:bg-bc-neut-bg hover:text-bc-text " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bc-soft";

const DIVIDER = "mx-0.5 h-5 w-px shrink-0 bg-bc-border";

/** `mousedown` is where focus is lost, so that is where it is stopped. */
const keepSelection = (event) => event.preventDefault();

/**
 * One toolbar button.
 *
 * @param {Object} props
 * @param {string} props.label - Accessible name; also the tooltip.
 * @param {boolean} [props.active] - Renders and announces the pressed state.
 * @param {() => void} props.onPress
 * @param {React.ReactNode} props.children
 * @param {string} [props.testId]
 * @returns {React.ReactElement}
 */
const ToolButton = ({ label, active, onPress, children, testId }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active === undefined ? undefined : active}
    data-testid={testId}
    onMouseDown={keepSelection}
    onClick={onPress}
    className={`${BUTTON} ${active ? BUTTON_ON : ""}`}
  >
    {children}
  </button>
);

/**
 * A button that opens a small panel beneath it.
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {boolean} props.open
 * @param {() => void} props.onToggle
 * @param {React.ReactNode} props.trigger - What the button shows.
 * @param {React.ReactNode} props.children - The panel's contents.
 * @param {string} [props.testId]
 * @returns {React.ReactElement}
 */
const ToolMenu = ({ label, open, onToggle, trigger, children, testId }) => (
  <div className="relative">
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={open}
      data-testid={testId}
      onMouseDown={keepSelection}
      onClick={onToggle}
      className={`${BUTTON} ${open ? BUTTON_ON : ""}`}
    >
      {trigger}
    </button>
    {open ? <div className={POPOVER}>{children}</div> : null}
  </div>
);

/**
 * The formatting row above the body.
 *
 * @param {Object} props
 * @param {(command: "bold"|"italic"|"underline") => void} props.onInline
 * @param {(command: "bold"|"italic"|"underline") => boolean} props.isActive - Asked at render time, so the buttons track the caret.
 * @param {number|null} [props.activeFontSize] - Pixel size at the caret, ticked in the size menu.
 * @param {(px: number) => void} props.onFontSize
 * @param {(value: string) => void} props.onTextColor
 * @param {(value: string) => void} props.onHighlight
 * @param {() => void} props.onClear
 * @param {() => void} props.onAddLink
 * @returns {React.ReactElement}
 */
export const EmailBodyToolbar = ({
  onInline,
  isActive,
  activeFontSize,
  onFontSize,
  onTextColor,
  onHighlight,
  onClear,
  onAddLink,
}) => {
  /** Which popover is open — one at a time, or `null`. */
  const [menu, setMenu] = useState(null);
  const toggle = (name) => setMenu((current) => (current === name ? null : name));
  /** Every pick closes the menu it came from; nothing stays open behind a click. */
  const pick = (action) => (value) => {
    setMenu(null);
    action(value);
  };

  return (
    <div
      role="toolbar"
      aria-label="Body formatting"
      data-testid="bc-email-toolbar"
      className="flex flex-wrap items-center gap-1 rounded-lg border border-bc-border bg-bc-subtle px-1.5 py-1 text-xs"
    >
      <ToolButton
        label="Bold"
        testId="bc-email-bold"
        active={isActive("bold")}
        onPress={() => onInline("bold")}
      >
        <FiBold aria-hidden="true" />
      </ToolButton>
      <ToolButton
        label="Italic"
        testId="bc-email-italic"
        active={isActive("italic")}
        onPress={() => onInline("italic")}
      >
        <FiItalic aria-hidden="true" />
      </ToolButton>
      <ToolButton
        label="Underline"
        testId="bc-email-underline"
        active={isActive("underline")}
        onPress={() => onInline("underline")}
      >
        <FiUnderline aria-hidden="true" />
      </ToolButton>

      <span aria-hidden="true" className={DIVIDER} />

      <ToolMenu
        label="Text size"
        testId="bc-email-size"
        open={menu === "size"}
        onToggle={() => toggle("size")}
        trigger={
          <span className="inline-flex items-center gap-1">
            <FiType aria-hidden="true" />
            {/* The number, not just the icon. A size control that cannot say
                what size you are on is a control you have to guess at. */}
            {activeFontSize ? (
              <span className="font-mono text-[10px] leading-none tabular-nums">
                {activeFontSize}
              </span>
            ) : null}
          </span>
        }
      >
        <div className="w-36 space-y-0.5">
          {EMAIL_FONT_SIZES.map((size) => (
            <button
              key={size.px}
              type="button"
              aria-pressed={activeFontSize === size.px}
              onMouseDown={keepSelection}
              onClick={() => pick(onFontSize)(size.px)}
              className={`${SIZE_ITEM} flex items-center justify-between gap-2 ${
                activeFontSize === size.px ? "bg-bc-neut-bg text-bc-text" : ""
              }`}
            >
              <span style={{ fontSize: `${Math.min(size.px, 18)}px` }}>
                {size.label}
              </span>
              <span className="font-mono text-[10px] text-bc-faint tabular-nums">
                {size.px}
              </span>
            </button>
          ))}
        </div>
      </ToolMenu>

      <ToolMenu
        label="Text colour"
        testId="bc-email-color"
        open={menu === "color"}
        onToggle={() => toggle("color")}
        trigger={
          <span className="font-semibold leading-none" aria-hidden="true">
            A
          </span>
        }
      >
        <div className="flex w-[164px] flex-wrap gap-1.5">
          {EMAIL_TEXT_COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              title={color.label}
              aria-label={color.label}
              onMouseDown={keepSelection}
              onClick={() => pick(onTextColor)(color.value)}
              className={SWATCH}
              style={{ backgroundColor: color.value }}
            />
          ))}
        </div>
      </ToolMenu>

      <ToolMenu
        label="Highlight"
        testId="bc-email-highlight"
        open={menu === "highlight"}
        onToggle={() => toggle("highlight")}
        trigger={<FiDroplet aria-hidden="true" />}
      >
        <div className="flex w-[164px] flex-wrap gap-1.5">
          {EMAIL_HIGHLIGHTS.map((color) => (
            <button
              key={color.value}
              type="button"
              title={color.label}
              aria-label={color.label}
              onMouseDown={keepSelection}
              onClick={() => pick(onHighlight)(color.value)}
              className={`${SWATCH} ${
                color.value === "transparent" ? "bg-bc-hatch" : ""
              }`}
              style={
                color.value === "transparent"
                  ? undefined
                  : { backgroundColor: color.value }
              }
            />
          ))}
        </div>
      </ToolMenu>

      <span aria-hidden="true" className={DIVIDER} />

      <ToolButton label="Add link" testId="bc-email-add-link" onPress={onAddLink}>
        <FiLink aria-hidden="true" />
      </ToolButton>
      <ToolButton
        label="Clear formatting"
        testId="bc-email-clear"
        onPress={onClear}
      >
        <FiRotateCcw aria-hidden="true" />
      </ToolButton>
    </div>
  );
};

export default EmailBodyToolbar;
